import { Box, Button, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { VisuallyHiddenInput } from "components/UiComponents";
import { kml } from "@tmcw/togeojson";
import { useState, useRef } from "react";
import { getReadableFileSize } from "utils/file";
import { useSnackbar } from "notistack";
import { ensureIds } from "utils/map";

export default function KmlFileUploadButton({
  label,
  file,
  setFile,
  setGeojson,
  maxFileSize = 0, // file size limit in bytes
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const clearFile = () => {
    setFile(null);
    setGeojson(null);
    setError("");
    setFileSize(0);
    // Clear the file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFile(file);
      const reader = new window.FileReader();
      reader.onload = (event) => {
        try {
          const parser = new DOMParser();
          const kmlDom = parser.parseFromString(
            event.target.result,
            "text/xml"
          );
          const geojsonData = kml(kmlDom);
          const fileSize = new TextEncoder().encode(
            JSON.stringify(geojsonData)
          ).length;
          setFileSize(fileSize);
          if (maxFileSize > 0 && fileSize > maxFileSize) {
            clearFile();
            enqueueSnackbar(
              `Uploaded file is too large. The limit is ${getReadableFileSize(maxFileSize)}.`,
              {
                variant: "error",
              }
            );
            return;
          }
          const geojson = ensureIds(geojsonData);
          setGeojson(geojson);
          setError(""); // Clear any previous errors
        } catch (err) {
          setError("Failed to convert KML to GeoJSON");
          console.error("Failed to convert KML to GeoJSON:", err);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Button
        variant="text"
        color="primary"
        component="label"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center",
          flex: "0 0 110px",
        }}
      >
        <FileUploadOutlinedIcon />
        <VisuallyHiddenInput
          ref={fileInputRef}
          type="file"
          accept=".kml"
          onChange={onFileChange}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            textTransform: "none",
          }}
        >
          {label} (KML)
        </Typography>
      </Button>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          flexDirection: "column",
          justifyContent: "center",
          mt: 1,
        }}
      >
        {file ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="caption"
                color="textSecondary"
                component="span"
              >
                {`${file.name} (${getReadableFileSize(fileSize)})`}
              </Typography>
            </Box>

            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Button
              size="small"
              color="error"
              onClick={clearFile}
              sx={{
                padding: "5px 6px 2px",
                minWidth: 0,
                lineHeight: 1,
              }}
            >
              Clear
            </Button>
          </>
        ) : (
          <Typography variant="caption" color="textSecondary">
            No {label} Uploaded
          </Typography>
        )}
      </Box>
    </Box>
  );
}
